<script>
  import Icon from "@iconify/svelte";
  import { t } from "./lib/i18n";

  import settingsIcon from "@iconify/icons-mdi/cog-outline";
  import clockIcon from "@iconify/icons-mdi/clock-outline";
  import timerIcon from "@iconify/icons-mdi/timer-outline";
  import alertIcon from "@iconify/icons-mdi/alert-circle-outline";
  import tagIcon from "@iconify/icons-mdi/tag-outline";

  export let apiBase = ""; // NEW (kept for parity / future use)
  export let apiKey = "";  // NEW

  export let setup = null;
  export let setupErr = "";

  function envValue(name) {
    const env = setup?.env || [];
    const hit = env.find((e) => e.name === name);
    return hit?.value || hit?.default || "";
  }

  function item(label, value, hint = "") {
    return { label, value, hint };
  }

  function section(title, items) {
    return { title, items };
  }

  function pretty(v) {
    const s = (v ?? "").toString().trim();
    return s ? s : "\u2014";
  }

  $: meta = setup?.meta || null;

  $: sections = [
    section("UI", [
      item("UI enabled", pretty(envValue("UI_ENABLED"))),
      item("UI dist dir", pretty(envValue("UI_DIST_DIR"))),
      item("UI auth username", pretty(envValue("GREENLIGHT_USERNAME"))),
      item("UI auth password", envValue("GREENLIGHT_PASSWORD") ? $t('common.set') : $t('common.notSet'))
    ]),

    section("API auth", [
      item("Shared API key", envValue("SHARED_API_KEY") ? $t('common.set') : $t('common.notSet')),
      item("Shared API secret", envValue("SHARED_API_SECRET") ? $t('common.set') : $t('common.notSet'))
    ]),

    section("Core worker", [
      item("Worker ID", pretty(envValue("WORKER_ID")), "defaults to hostname"),
      item("Max workers", pretty(envValue("MAX_WORKERS"))),
      item("Job timeout", pretty(envValue("JOB_TIMEOUT"))),
      item("HTTP port", pretty(envValue("HTTP_PORT"))),
      item("Max workers cap", pretty(envValue("MAX_WORKERS_CAP")))
    ]),

    section("ScreenJSON tools", [
      item("screenjson", pretty(envValue("SCREENJSON_BIN"))),
      item("pdftohtml", pretty(envValue("PDFTOHTML_BIN")))
    ]),

    section("Queue", [
      item("Backend", pretty(envValue("QUEUE_BACKEND"))),
      item("Redis address", pretty(envValue("REDIS_ADDR"))),
      item("Redis password", envValue("REDIS_PASSWORD") ? $t('common.set') : $t('common.notSet')),
      item("Input queue", pretty(envValue("REDIS_INPUT_QUEUE"))),
      item("Results queue", pretty(envValue("REDIS_RESULTS_QUEUE"))),
      item("Results TTL", pretty(envValue("REDIS_RESULTS_TTL")), "hours (e.g. 2h or 2)")
    ]),

    section("Storage", [
      item("Backend", pretty(envValue("STORAGE_BACKEND")), "minio | s3 | noop"),

      item("S3 endpoint", pretty(envValue("S3_ENDPOINT"))),
      item("S3 region", pretty(envValue("S3_REGION"))),
      item("Use SSL", pretty(envValue("S3_USE_SSL"))),
      item("Force path style", pretty(envValue("S3_FORCE_PATH_STYLE"))),

      item("Access key id", envValue("S3_ACCESS_KEY_ID") ? $t('common.set') : $t('common.notSet')),
      item("Secret access key", envValue("S3_SECRET_ACCESS_KEY") ? $t('common.set') : $t('common.notSet')),

      item("Input bucket", pretty(envValue("S3_INPUT_BUCKET"))),
      item("Output bucket", pretty(envValue("S3_OUTPUT_BUCKET"))),
      item("Input prefix", pretty(envValue("S3_INPUT_PREFIX"))),
      item("Output prefix", pretty(envValue("S3_OUTPUT_PREFIX")))
    ]),


    section("Webhooks", [
      item("Default webhooks", pretty(envValue("DEFAULT_WEBHOOKS"))),
      item("Timeout", pretty(envValue("WEBHOOK_TIMEOUT"))),
      item("Max retry", pretty(envValue("WEBHOOK_MAX_RETRY"))),
      item("Retry min wait", pretty(envValue("WEBHOOK_RETRY_MIN_WAIT"))),
      item("Retry max wait", pretty(envValue("WEBHOOK_RETRY_MAX_WAIT"))),
      item("Secret", envValue("WEBHOOK_SECRET") ? $t('common.set') : $t('common.notSet'))
    ])
  ];

  // No fetches in this component. We still accept apiKey so App can pass it consistently.
  // If auth is removed mid-session, App will swap to Login anyway.

  const mono =
    "font-mono text-xs text-zinc-200 whitespace-nowrap overflow-hidden text-ellipsis";

  const sectionCard =
    "rounded-2xl border border-white/10 bg-black/20 p-4 sm:p-5";

  const cell =
    "rounded-xl border border-white/10 bg-white/[0.03] p-3 min-w-0";

  const klabel = "text-xs text-zinc-400 flex flex-wrap items-center gap-2";
  const khint =
    "text-[11px] text-zinc-500 inline-flex items-center gap-1 whitespace-nowrap";

  const headerChip =
    "rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-xs text-zinc-300 inline-flex items-center gap-2";
</script>

<main class="w-full px-4 py-6">
  <section class="ui-card p-5 sm:p-6">
    <!-- Header -->
    <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div class="flex items-start gap-3">
        <span
          class="inline-flex h-9 w-9 items-center justify-center border border-pink-400/50 bg-pink-500/30 text-pink-50"
        >
          <Icon icon={settingsIcon} width="18" height="18" />
        </span>

        <div>
          <h1 class="text-lg font-normal tracking-wide text-zinc-100">
            {$t('settings.title')}
          </h1>
          <p class="mt-0.5 text-sm text-zinc-400">
            {$t('settings.subtitle')}
          </p>
        </div>
      </div>

      <div class="flex flex-wrap gap-2">
        {#if meta?.now_utc}
          <div class={headerChip} title={$t('settings.serverTime')}>
            <Icon icon={clockIcon} width="16" height="16" />
            <span class="text-zinc-400">{$t('settings.serverTime')}</span>
            <span class="font-medium text-zinc-200">{meta.now_utc}</span>
          </div>
        {/if}

        {#if meta?.uptime}
          <div class={headerChip} title={$t('settings.uptime')}>
            <Icon icon={timerIcon} width="16" height="16" />
            <span class="text-zinc-400">{$t('settings.uptime')}</span>
            <span class="font-medium text-zinc-200">{meta.uptime}</span>
          </div>
        {/if}
      </div>
    </div>

    <!-- Error / Loading -->
    {#if setupErr}
      <div
        class="mt-4 rounded-xl border border-rose-400/35 bg-rose-400/10 px-4 py-3 text-sm text-rose-100 flex items-center gap-2"
      >
        <Icon icon={alertIcon} width="18" height="18" />
        <span class="break-words">{setupErr}</span>
      </div>
    {/if}

    {#if !setup}
      <div
        class="mt-6 rounded-xl border border-white/10 bg-black/20 p-4 text-sm text-zinc-400"
      >
        {$t('common.loading')}
      </div>
    {:else}
      <!-- Sections -->
      <div class="mt-6 grid grid-cols-1 gap-4">
        {#each sections as s (s.title)}
          <div class={sectionCard}>
            <div
              class="flex items-baseline justify-between gap-3 border-b border-white/10 pb-3"
            >
              <div class="flex items-center gap-2">
                <Icon icon={tagIcon} width="16" height="16" />
                <div class="text-sm font-semibold tracking-wide text-white">
                  {s.title}
                </div>
              </div>

              <div class="text-xs text-zinc-500">{s.items.length} {$t('common.items')}</div>
            </div>

            <div
              class="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3"
            >
              {#each s.items as it (it.label)}
                <div class={cell}>
                  <div class={klabel}>
                    <span>{it.label}</span>
                    {#if it.hint}
                      <span class={khint}>&bull; {it.hint}</span>
                    {/if}
                  </div>

                  <div class={`${mono} mt-2`} title={it.value}>
                    {it.value}
                  </div>
                </div>
              {/each}
            </div>
          </div>
        {/each}
      </div>
    {/if}
  </section>
</main>
